import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Mail, Plus, Save } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import { MedicalFormRenderer } from './MedicalFormRenderer';
import { MedicalFormInstance, MedicalFormReviewStatus, MedicalFormTemplate } from '../types/medicalForms';

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-700',
    SENT: 'bg-blue-100 text-blue-700',
    IN_PROGRESS: 'bg-amber-100 text-amber-800',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    CANCELLED: 'bg-rose-100 text-rose-700',
    EXPIRED: 'bg-slate-100 text-slate-600',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

function reviewStatusBadge(status: string) {
  const map: Record<string, string> = {
    NOT_REVIEWED: 'bg-slate-100 text-slate-700',
    IN_REVIEW: 'bg-amber-100 text-amber-800',
    REVIEWED: 'bg-emerald-100 text-emerald-700',
    NEEDS_FOLLOW_UP: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

type CreateInstanceResponse = MedicalFormInstance & {
  delivery?: {
    mode: 'mock' | 'webhook';
    formUrl: string | null;
    recipient: string | null;
  } | null;
};

export function PatientMedicalFormsModule({
  patientId,
  patientEmail,
}: {
  patientId: string;
  patientEmail?: string | null;
}) {
  const { fetchApi, fetchJson } = useApiClient();
  const { tr } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState<MedicalFormTemplate[]>([]);
  const [instances, setInstances] = useState<MedicalFormInstance[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<MedicalFormInstance | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [createTemplateId, setCreateTemplateId] = useState('');
  const [createDueDate, setCreateDueDate] = useState('');
  const [createSendEmail, setCreateSendEmail] = useState(false);
  const [createEmail, setCreateEmail] = useState(patientEmail || '');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState<MedicalFormReviewStatus>('NOT_REVIEWED');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  const selectedTemplateDefinition = selectedInstance?.template.definition;

  const summary = useMemo(() => {
    return {
      total: instances.length,
      completed: instances.filter((item) => item.status === 'COMPLETED').length,
      pending: instances.filter((item) => item.status !== 'COMPLETED').length,
    };
  }, [instances]);

  const loadTemplates = useCallback(async () => {
    const data = await fetchJson<MedicalFormTemplate[]>('/medical-forms/templates');
    setTemplates(data.filter((item) => item.status !== 'ARCHIVED'));
    if (!createTemplateId && data.length) {
      setCreateTemplateId(data[0].id);
    }
  }, [createTemplateId, fetchJson]);

  const loadInstances = useCallback(async () => {
    const data = await fetchJson<MedicalFormInstance[]>(`/medical-forms/patients/${patientId}/instances`);
    setInstances(data);
    if (data.length && !selectedId) {
      setSelectedId(data[0].id);
    }
    if (!data.length) {
      setSelectedId(null);
      setSelectedInstance(null);
      setAnswers({});
    }
  }, [fetchJson, patientId, selectedId]);

  const loadInstanceDetail = useCallback(
    async (instanceId: string) => {
      const detail = await fetchJson<MedicalFormInstance>(`/medical-forms/instances/${instanceId}`);
      setSelectedInstance(detail);
      setAnswers(detail.answers || {});
    },
    [fetchJson],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([loadTemplates(), loadInstances()])
      .catch((err: any) => {
        if (active) setError(err.message || tr('Unable to load patient forms.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadInstances, loadTemplates]);

  useEffect(() => {
    if (!selectedId) return;
    loadInstanceDetail(selectedId).catch((err: any) => {
      setError(err.message || tr('Unable to load selected form.'));
    });
  }, [loadInstanceDetail, selectedId, tr]);

  useEffect(() => {
    setReviewStatus(selectedInstance?.reviewStatus || 'NOT_REVIEWED');
    setReviewNotes(selectedInstance?.reviewNotes || '');
  }, [selectedInstance]);

  useEffect(() => {
    if (!createDialogOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCreateDialogOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [createDialogOpen]);

  function openCreateDialog() {
    setError(null);
    setBanner(null);
    setCreateDueDate('');
    setCreateSendEmail(false);
    setCreateEmail(patientEmail || '');
    if (!createTemplateId && templates.length) setCreateTemplateId(templates[0].id);
    setCreateDialogOpen(true);
  }

  async function createInstance() {
    if (!createTemplateId) {
      setError(tr('Select a form template first.'));
      return;
    }
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const created = await fetchJson<CreateInstanceResponse>(`/medical-forms/patients/${patientId}/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: createTemplateId,
          dueDate: createDueDate ? new Date(createDueDate).toISOString() : undefined,
          sendToPatientEmail: createSendEmail,
          email: createEmail || undefined,
        }),
      });
      await loadInstances();
      setSelectedId(created.id);
      setBanner(
        created.delivery?.formUrl
          ? `${tr('Form created and sent via')} ${created.delivery.mode}. ${tr('Link')}: ${created.delivery.formUrl}`
          : tr('Form instance created.'),
      );
      setCreateDialogOpen(false);
    } catch (err: any) {
      setError(err.message || tr('Unable to create form instance.'));
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft(status?: MedicalFormInstance['status']) {
    if (!selectedInstance) return;
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const updated = await fetchJson<MedicalFormInstance>(`/medical-forms/instances/${selectedInstance.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          status: status || undefined,
        }),
      });
      setSelectedInstance(updated);
      setAnswers(updated.answers || {});
      await loadInstances();
      setBanner(status === 'COMPLETED' ? tr('Form marked complete.') : tr('Form saved.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to save form.'));
    } finally {
      setSaving(false);
    }
  }

  async function sendEmail() {
    if (!selectedInstance) return;
    setSending(true);
    setError(null);
    setBanner(null);
    try {
      const recipient = window.prompt(tr('Send form to email:'), createEmail || patientEmail || '');
      if (!recipient) {
        setSending(false);
        return;
      }
      const payload = await fetchJson<{ delivery: { mode: string; formUrl: string; recipient: string } }>(
        `/medical-forms/instances/${selectedInstance.id}/send-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: recipient }),
        },
      );
      setCreateEmail(recipient);
      await loadInstances();
      await loadInstanceDetail(selectedInstance.id);
      setBanner(`${tr('Form sent')} (${payload.delivery.mode}) ${tr('to')} ${payload.delivery.recipient}. ${tr('Link')}: ${payload.delivery.formUrl}`);
    } catch (err: any) {
      setError(err.message || tr('Unable to send form.'));
    } finally {
      setSending(false);
    }
  }

  async function saveReview() {
    if (!selectedInstance) return;
    setReviewSaving(true);
    setError(null);
    setBanner(null);
    try {
      const updated = await fetchJson<MedicalFormInstance>(`/medical-forms/instances/${selectedInstance.id}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reviewStatus,
          reviewNotes,
        }),
      });
      setSelectedInstance(updated);
      setReviewStatus(updated.reviewStatus);
      setReviewNotes(updated.reviewNotes || '');
      await loadInstances();
      setBanner(tr('Form review updated.'));
    } catch (err: any) {
      setError(err.message || tr('Unable to update form review.'));
    } finally {
      setReviewSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Medical Forms')}</p>
          <h3 className="text-lg font-semibold text-slate-900">{tr('Patient Forms Workspace')}</h3>
          <p className="text-xs text-slate-500">{tr('Assign reusable forms, complete in clinic, or send secure links to patients before arrival.')}</p>
        </div>
        <div className="flex items-center justify-end">
          <button
            onClick={openCreateDialog}
            className="inline-flex items-center gap-2 rounded-lg bg-lime-400 px-3 py-2 text-sm text-slate-900 hover:bg-lime-300"
          >
            <Plus size={14} />
            {tr('Create Form')}
          </button>
        </div>
      </div>

      {banner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{banner}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-3 border-b border-slate-100 text-sm text-slate-600">
            {summary.total} {tr('total')} • {summary.completed} {tr('completed')} • {summary.pending} {tr('pending')}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{tr('Form')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Status')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Review')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Updated')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Details')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> {tr('Loading forms...')}
                      </span>
                    </td>
                  </tr>
                ) : instances.length ? (
                  instances.map((instance) => (
                    <tr
                      key={instance.id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === instance.id ? 'bg-slate-50' : ''}`}
                      onClick={() => setSelectedId(instance.id)}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-slate-800">{instance.template.name}</div>
                        {instance.sentToEmail ? <div className="mt-1 text-xs text-blue-600">{tr('Sent to')} {instance.sentToEmail}</div> : null}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`text-[11px] px-2 py-1 rounded-full ${statusBadge(instance.status)}`}>{tr(instance.status)}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`text-[11px] px-2 py-1 rounded-full ${reviewStatusBadge(instance.reviewStatus)}`}>{tr(instance.reviewStatus)}</span>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-600">{formatDateTime(instance.updatedAt)}</td>
                      <td className="px-4 py-3 align-top text-slate-600">
                        <div>{tr('Completion')} {instance.completionPercent}%</div>
                        <div className="text-xs text-slate-500">{tr('Due')} {formatDateTime(instance.dueDate)}</div>
                        <div className="text-xs text-slate-500">{tr('Source')} {instance.source || '-'}</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="p-4 text-sm text-slate-500">
                      {tr('No medical form instances for this patient yet.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="xl:col-span-6 rounded-xl border border-slate-200 bg-white p-4 space-y-4">
          {!selectedInstance || !selectedTemplateDefinition ? (
            <div className="text-sm text-slate-500">{tr('Select a form instance to start filling.')}</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-lg font-semibold text-slate-900">{selectedInstance.template.name}</p>
                  <p className="text-xs text-slate-500">
                    {tr('Status')} {tr(selectedInstance.status)} • {tr('Completion')} {selectedInstance.completionPercent}% • {tr('Due')} {formatDateTime(selectedInstance.dueDate)}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`text-[11px] px-2 py-1 rounded-full ${reviewStatusBadge(selectedInstance.reviewStatus)}`}>{tr(selectedInstance.reviewStatus)}</span>
                    <span className="text-xs text-slate-500">{tr('Source')} {selectedInstance.source || '-'}</span>
                    <span className="text-xs text-slate-500">{tr('Reviewed')} {formatDateTime(selectedInstance.reviewedAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => saveDraft()} disabled={saving} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm disabled:opacity-60">
                    <Save size={14} className="inline mr-1" /> {tr('Save')}
                  </button>
                  <button onClick={sendEmail} disabled={sending} className="px-3 py-2 rounded border border-slate-300 bg-white text-sm disabled:opacity-60">
                    <Mail size={14} className="inline mr-1" /> {sending ? tr('Sending...') : tr('Send Email')}
                  </button>
                  <button onClick={() => saveDraft('COMPLETED')} disabled={saving} className="px-3 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-60">
                    <CheckCircle2 size={14} className="inline mr-1" /> {tr('Complete')}
                  </button>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{tr('Review Workflow')}</p>
                    <p className="text-xs text-slate-500">{tr('Track staff review separately from patient completion so intake follow-up is visible in the chart.')}</p>
                  </div>
                  {selectedInstance.reviewedBy ? (
                    <div className="text-xs text-slate-500">
                      {tr('Last reviewer')}: {selectedInstance.reviewedBy.firstName} {selectedInstance.reviewedBy.lastName}
                    </div>
                  ) : null}
                </div>
                <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                  <label className="space-y-1 text-sm text-slate-700">
                    <span>{tr('Review Status')}</span>
                    <select value={reviewStatus} onChange={(event) => setReviewStatus(event.target.value as MedicalFormReviewStatus)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value="NOT_REVIEWED">{tr('NOT_REVIEWED')}</option>
                      <option value="IN_REVIEW">{tr('IN_REVIEW')}</option>
                      <option value="REVIEWED">{tr('REVIEWED')}</option>
                      <option value="NEEDS_FOLLOW_UP">{tr('NEEDS_FOLLOW_UP')}</option>
                    </select>
                  </label>
                  <label className="space-y-1 text-sm text-slate-700">
                    <span>{tr('Review Notes')}</span>
                    <textarea
                      value={reviewNotes}
                      onChange={(event) => setReviewNotes(event.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder={tr('Document missing information, acknowledgements, or follow-up instructions for the care team.')}
                    />
                  </label>
                </div>
                <div className="flex justify-end">
                  <button onClick={saveReview} disabled={reviewSaving} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-60">
                    {reviewSaving ? tr('Saving...') : tr('Save Review')}
                  </button>
                </div>
              </div>
              {selectedInstance.signatureMetadata && Object.keys(selectedInstance.signatureMetadata).length > 0
                ? (() => {
                    const sig = selectedInstance.signatureMetadata as Record<string, unknown>;
                    const line = (label: string, value: unknown) =>
                      value ? (
                        <span>
                          {label}: <span className="font-medium">{String(value)}</span>
                        </span>
                      ) : null;
                    return (
                      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
                        <div className="font-semibold text-emerald-800">✓ {tr('Signed by patient')}</div>
                        <div className="mt-1 grid grid-cols-1 gap-x-6 gap-y-1 text-emerald-900/80 sm:grid-cols-2">
                          {line(tr('Signed by'), sig.signerName)}
                          {sig.capturedAt ? <span>{tr('Signed on')}: <span className="font-medium">{formatDateTime(String(sig.capturedAt))}</span></span> : null}
                          {line(tr('Method'), sig.signatureMode)}
                          {line(tr('Source'), sig.source)}
                          {line('IP', sig.ipAddress)}
                        </div>
                      </div>
                    );
                  })()
                : null}
              <MedicalFormRenderer definition={selectedTemplateDefinition} answers={answers} onChange={(fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))} />
            </>
          )}
        </div>
      </div>

      {createDialogOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4" onClick={() => setCreateDialogOpen(false)}>
          <div className="mt-8 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Medical Forms')}</p>
                <h4 className="text-lg font-semibold text-slate-900">{tr('Create Form Instance')}</h4>
              </div>
              <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700" onClick={() => setCreateDialogOpen(false)}>
                {tr('Close')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <select className="md:col-span-2 border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createTemplateId} onChange={(event) => setCreateTemplateId(event.target.value)}>
                <option value="">{tr('Select template...')}</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.status})
                  </option>
                ))}
              </select>
              <input type="datetime-local" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createDueDate} onChange={(event) => setCreateDueDate(event.target.value)} />
              <input type="email" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={tr('Patient email (optional)')} value={createEmail} onChange={(event) => setCreateEmail(event.target.value)} />
            </div>
            <label className="mt-2 text-sm text-slate-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={createSendEmail} onChange={(event) => setCreateSendEmail(event.target.checked)} />
              {tr('Send immediately by email')}
            </label>
            <div className="mt-3 flex items-center gap-2">
              <button className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" onClick={() => setCreateDialogOpen(false)}>
                {tr('Cancel')}
              </button>
              <button
                onClick={createInstance}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-lime-400 px-3 py-2 text-sm text-slate-900 hover:bg-lime-300 disabled:opacity-60"
              >
                <Plus size={14} />
                {saving ? tr('Creating...') : tr('Create Form')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
